<template>
  <div class="max-w-full mx-auto px-6 py-6 dark:bg-gray-700 dark:border-gray-600">
    <h2 class="text-2xl font-semibold mb-5 dark:text-gray-100 text-center">
      {{ isEdit ? `Edit Job #${form.id}` : 'Add New Job' }}
    </h2>

    <div class="grid grid-cols-1 md:grid-cols-2 gap-12">
      <div class="space-y-4">
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
          <!-- Client -->
          <div class="md:col-span-2" v-click-outside="closeClientDropdown">
            <label class="block text-xs font-medium text-gray-700 dark:text-gray-100 mr-4">Client</label>
            <div class="relative">
              <input type="text" ref="clientInput" @focus="dropdownState.client.visible = true"
                @keydown.down.prevent="onKeyDown('client', 'down')" @keydown.up.prevent="onKeyDown('client', 'up')"
                @keydown.enter.prevent="onEnter('client', setClient)" :class="[
                  'mt-1 block w-full text-xs rounded-md shadow-sm',
                  validationErrors.company_id ? 'border-red-500 border' : 'border-gray-300 dark:border-gray-900',
                  'dark:bg-gray-400 dark:text-gray-100'
                ]" placeholder="Client Name" v-model="form.companyname" />
              <button v-if="form.company_id != '' && form.companyname !== ''" @click="clearClient"
                class="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-red-500">
                <svg class="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M18 6L6 18" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                    stroke-linejoin="round" />
                  <path d="M6 6L18 18" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                    stroke-linejoin="round" />
                </svg>
              </button>
            </div>
            <ul v-if="dropdownState.client.visible" data-field="client"
              class="absolute z-10 bg-white mt-1 rounded-md shadow-lg w-100 dark:bg-gray-400 dark:border-gray-900 dark:text-gray-100 scrollable-requester-list">
              <li v-for="(client, index) in filteredClients" :key="client.id" @click="setClient(client)" :class="[
                'cursor-pointer p-3',
                dropdownState.client.index === index ? 'bg-blue-100 dark:bg-gray-700 active-item' : 'hover:bg-gray-100 dark:hover:bg-gray-800'
              ]">
                {{ client.companyname }}<span v-if="client.active == 0" class="ml-2 text-xs italic text-gray-400">(inactive)</span>
              </li>
            </ul>
          </div>

          <!-- Requester -->
          <div class="md:col-span-2" v-click-outside="closeReqDropdown">
            <label class="block text-xs font-medium text-gray-700 dark:text-gray-100 mr-4">Requester</label>
            <div class="relative">
              <input type="text"
                :class="[
                  'mt-1 text-xs block w-full rounded-md shadow-sm dark:bg-gray-400 dark:text-gray-100',
                  validationErrors.contact_id ? 'border-red-500 border' : 'border-gray-300 dark:border-gray-900'
                ]"
                placeholder="Select Requester" v-model="form.requester" @focus="dropdownState.requester.visible = true"
                @keydown.down.prevent="onKeyDown('requester', 'down')"
                @keydown.up.prevent="onKeyDown('requester', 'up')"
                @keydown.enter.prevent="onEnter('requester', setReq)" />
              <button v-if="form.requester !== '' && form.contact_id != ''" @click="clearReq"
                class="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-red-500">
                <svg class="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M18 6L6 18" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                    stroke-linejoin="round" />
                  <path d="M6 6L18 18" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                    stroke-linejoin="round" />
                </svg>
              </button>
            </div>
            <ul v-if="dropdownState.requester.visible" data-field="requester"
              class="absolute z-10 bg-white mt-1 rounded-md shadow-lg w-100 dark:bg-gray-400 dark:border-gray-900 dark:text-gray-100 scrollable-requester-list">
              <li v-for="(req, index) in filteredRequesters" :key="req.id" @click="setReq(req, false)" :class="[
                'cursor-pointer p-3',
                dropdownState.requester.index === index ? 'bg-blue-100 dark:bg-gray-700 active-item' : 'hover:bg-gray-100 dark:hover:bg-gray-800'
              ]">
                {{ req.name }}<span v-if="req.email"> - {{ req.email }}</span><span v-if="req.user_type"> ({{ req.user_type }})</span><span
                  v-if="requesterCompanyLabel(req)" :title="requesterCompanyTitle(req)"> ({{ requesterCompanyLabel(req) }})</span>
              </li>
            </ul>
          </div>

          <!-- Job Type -->
          <div>
            <label class="block text-xs font-medium text-gray-700 dark:text-gray-100">Job Type</label>
            <select v-model="form.jobtype_id" :class="[
              'mt-1 block text-xs w-full rounded-md shadow-sm',
              validationErrors.jobtype_id ? 'border-red-500 border' : 'border-gray-300 dark:border-gray-900',
              'dark:bg-gray-400 dark:text-gray-100'
            ]">
              <option value="">Select</option>
              <option v-for="type in jobTypes" :key="type.id" :value="type.id">{{ type.name }}</option>
            </select>
          </div>
          <div>
            <label class="block text-xs font-medium text-gray-700 dark:text-gray-100">Date</label>
            <input type="date" v-model="form.date"
              class="mt-1 block text-xs w-full rounded-md border-gray-300 shadow-sm dark:bg-gray-400 dark:border-gray-900 dark:text-gray-100">
          </div>
          <div>
            <label class="block text-xs font-medium text-gray-700 dark:text-gray-100">Total Time</label>
            <input type="number" v-model="form.total_time" step="0.05" min="0"
              class="mt-1 block text-xs w-full rounded-md border-gray-300 shadow-sm dark:bg-gray-400 dark:border-gray-900 dark:text-gray-100">
          </div>
          <div>
            <label class="block text-xs text-xs font-medium text-gray-700 dark:text-gray-100">Ignorance/NC
              Portion</label>
            <input type="number" v-model="form.IA" step="0.05" min="0"
              class="mt-1 block w-full rounded-md border-gray-300 shadow-sm dark:bg-gray-400 dark:border-gray-900 dark:text-gray-100">
          </div>
          <div class="md:col-span-2 relative">
            <label class="block text-xs font-medium text-gray-700 dark:text-gray-100">Documentation / Notes</label>
            <div ref="toastEditor" :class="[
              'dark:bg-gray-100',
              validationErrors.description ? 'border border-red-500 rounded' : ''
            ]"></div>
          </div>
        </div>
      </div>

      <div class="space-y-4">
        <div class="md:col-span-2 mt-2" v-if="userroles.some(r => [10, 20, 30, 40].includes(r)) && form.company_id">
          <!-- Warning -->
          <div v-if="clientNotes.warning"
            class="mb-2 p-3 bg-red-100 border-2 border-red-500 rounded shadow-md dark:bg-red-800 dark:border-red-400">
            <h3 class="text-xs font-semibold text-gray-900 dark:text-gray-100">
              ⚠ Warnings / Account Notices
            </h3>
            <pre class="mt-1 text-[11px] leading-snug text-gray-800 dark:text-gray-100 whitespace-pre-wrap">{{ clientNotes.warning }}
            </pre>
          </div>

          <!-- General Notes -->
          <div v-if="clientNotes.notes"
            class="mb-2 p-3 bg-yellow-100 border-2 border-yellow-500 rounded shadow-md dark:bg-yellow-800 dark:border-yellow-400">
            <h3 class="text-xs font-semibold text-gray-900 dark:text-gray-100">
              Client Notes
            </h3>
            <pre class="mt-1 text-[11px] leading-snug text-gray-800 dark:text-gray-100 whitespace-pre-wrap">{{ clientNotes.notes }}
            </pre>
          </div>
        </div>
        <div class="md:col-span-2" v-if="!userroles.includes(80)">
          <div
            class="bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg p-4 space-y-4 shadow-sm mt-6">
            <h3 class="text-md font-semibold text-gray-700 dark:text-gray-100 border-b pb-2">Onsite Details</h3>

            <!-- Source (Office) Location -->
            <div class="relative" v-click-outside="closeOfficeDropdown">
              <label class="block text-xs font-medium text-gray-700 dark:text-gray-100">Source Location</label>
              <div class="relative">
                <input type="text" v-model="form.officeLocationInput" @focus="dropdownState.office.visible = true"
                  @input="onOfficeInputChange" @keydown.down.prevent="onKeyDown('office', 'down')"
                  @keydown.up.prevent="onKeyDown('office', 'up')"
                  @keydown.enter.prevent="onEnter('office', setOfficeLocation)"
                  class="mt-1 text-xs block w-full rounded-md border-gray-300 shadow-sm dark:bg-gray-400 dark:border-gray-900 dark:text-gray-100"
                  placeholder="Start typing to search AIM or Google..." />
                <button v-if="form.officeLocationInput !== ''" @click="clearOfficeLocation"
                  class="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-red-500">
                  <svg class="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M18 6L6 18" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                      stroke-linejoin="round" />
                    <path d="M6 6L18 18" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                      stroke-linejoin="round" />
                  </svg>
                </button>
              </div>

              <ul v-if="dropdownState.office.visible" data-field="office"
                class="absolute z-10 bg-white mt-1 rounded-md shadow-lg w-full dark:bg-gray-400 dark:border-gray-900 dark:text-gray-100 max-h-60 overflow-y-auto scrollable-requester-list">
                <li v-for="(loc, index) in filteredOfficeLocations" :key="loc._key || loc.id"
                  @click="setOfficeLocation(loc)" :class="[
                    'cursor-pointer p-3 border-b border-gray-100 dark:border-gray-600',
                    dropdownState.office.index === index
                      ? 'bg-blue-100 dark:bg-gray-700 active-item'
                      : 'hover:bg-gray-100 dark:hover:bg-gray-800'
                  ]">
                  <div class="flex flex-col">
                    <span>
                      {{ loc.location_name }} - {{ loc.full_address || loc.address }}
                    </span>
                    <span v-if="loc.source === 'google'"
                      class="text-[10px] uppercase tracking-wide text-blue-600 dark:text-blue-300 mt-1">
                      Google Location
                    </span>
                    <span v-else class="text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-300 mt-1">
                      AIM location
                    </span>
                  </div>
                </li>
              </ul>
            </div>

            <!-- Destination Location -->
            <div class="relative" v-click-outside="closeDestDropdown">
              <label class="block text-xs font-medium text-gray-700 dark:text-gray-100">Client Location</label>
              <div class="relative">
                <input type="text" v-model="form.destinationLocationInput" @focus="dropdownState.dest.visible = true"
                  @input="onDestinationInputChange" @keydown.down.prevent="onKeyDown('dest', 'down')"
                  @keydown.up.prevent="onKeyDown('dest', 'up')"
                  @keydown.enter.prevent="onEnter('dest', setDestinationLocation)"
                  class="mt-1 text-xs block w-full rounded-md border-gray-300 shadow-sm dark:bg-gray-400 dark:border-gray-900 dark:text-gray-100"
                  placeholder="Search destination..." />
                <button v-if="form.destinationLocationInput !== ''" @click="clearDestinationLocation"
                  class="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-red-500">
                  <svg class="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M18 6L6 18" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                      stroke-linejoin="round" />
                    <path d="M6 6L18 18" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                      stroke-linejoin="round" />
                  </svg>
                </button>
              </div>
              <ul v-if="dropdownState.dest.visible" data-field="dest"
                class="absolute z-10 bg-white mt-1 rounded-md shadow-lg w-full dark:bg-gray-400 dark:border-gray-900 dark:text-gray-100 max-h-60 overflow-y-auto scrollable-requester-list">
                <li v-for="(loc, index) in filteredClientLocations" :key="`${loc.address}_${index}`"
                  @click="setDestinationLocation(loc)" :class="[
                    'cursor-pointer p-3 border-b border-gray-100 dark:border-gray-600',
                    dropdownState.dest.index === index
                      ? 'bg-blue-100 dark:bg-gray-700 active-item'
                      : 'hover:bg-gray-100 dark:hover:bg-gray-800'
                  ]">
                  <div class="flex flex-col">
                    <span>
                      {{ loc.id === 'other' ? loc.location_name : `${loc.location_name} - ${loc.address}` }}
                    </span>
                    <span v-if="loc.source === 'google'"
                      class="text-[10px] uppercase tracking-wide text-blue-600 dark:text-blue-300 mt-1">
                      Google result
                    </span>
                    <span v-else class="text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-300 mt-1">
                      Client Location From ATMS
                    </span>
                  </div>
                </li>
              </ul>
            </div>

            <!-- Trip Type + Mileage -->
            <div>
              <label class="block text-xs font-medium text-gray-700 dark:text-gray-100">Trip Type</label>
              <div class="mt-2 flex items-center space-x-4 text-xs dark:text-gray-100">
                <label><input type="radio" value="oneWay" v-model="form.tripType" @change="calculateMileageViaMapsJS"
                    class="text-blue-600 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-500" /> One Way</label>
                <label><input type="radio" value="round" v-model="form.tripType" @change="calculateMileageViaMapsJS"
                    class="text-blue-600 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-500" /> Round
                  Trip</label>
                <label><input type="radio" value="none" v-model="form.tripType" @change="calculateMileageViaMapsJS"
                    class="text-blue-600 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-500" /> None/In
                  Route</label>
              </div>
            </div>
            <!-- Company Vehicle -->
            <label class="flex items-center space-x-3">
              <input type="checkbox" v-model="form.companyVehicle" true-value="yes" false-value="no"
                class="h-5 w-5 text-blue-600 rounded focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-500" />
              <span class="text-xs font-medium text-gray-700 dark:text-gray-100">Company Vehicle</span>
            </label>

            <!-- Mileage and Travel Time -->
            <div class="grid grid-cols-2 gap-4">
              <div>
                <label class="block text-xs font-medium text-gray-700 dark:text-gray-100">Mileage</label>
                <input type="number" v-model="form.miles" min="0" step="0.1"
                  class="mt-1 block w-full text-xs rounded-md border-gray-300 shadow-sm dark:bg-gray-400 dark:border-gray-900 dark:text-gray-100" />
              </div>
              <div>
                <label class="block text-xs font-medium text-gray-700 dark:text-gray-100">Travel Time (hrs)</label>
                <input type="number" v-model="form.travelTime" min="0" step="0.1"
                  class="mt-1 block text-xs w-full rounded-md border-gray-300 shadow-sm dark:bg-gray-400 dark:border-gray-900 dark:text-gray-100" />
              </div>
            </div>

            <div class="text-center mt-4">
              <button @click="viewGoogleMap"
                class="w-full px-4 py-2 bg-blue-600 hover:bg-blue-800 text-white rounded-md font-medium">
                View Map & Directions
              </button>
            </div>
          </div>

        </div>

        <div class="md:col-span-2">
          <div
            class="bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg p-4 space-y-4 shadow-sm">
            <h3 class="text-md font-semibold text-gray-700 dark:text-gray-100 border-b pb-2">Job Options</h3>

            <!-- Start -->
            <label class="flex items-center space-x-3">
              <input type="checkbox" v-model="form.startNow"
                class="h-5 w-5 text-blue-600 rounded focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-500" />
              <span class="text-xs font-medium text-gray-700 dark:text-gray-100">Start</span>
            </label>

            <!-- Publish -->
            <label v-if="form.ticket_id > 0" class="flex items-center space-x-3">
              <input type="checkbox" v-model="form.publish"
                class="h-5 w-5 text-red-500 rounded focus:ring-red-500 dark:bg-gray-700 dark:border-gray-500" />
              <span class="text-xs font-medium text-red-600 dark:text-red-300">Publish</span>
            </label>

            <!-- Outside Scope -->
            <label class="flex items-center space-x-3">
              <input type="checkbox" v-model="form.is911"
                class="h-5 w-5 text-red-600 rounded focus:ring-red-600 dark:bg-gray-700 dark:border-gray-500" />
              <span class="text-xs font-medium text-red-600 dark:text-red-400">Outside Hours / Scope / Agreement</span>
            </label>

            <!-- Private -->
            <label class="flex items-center space-x-3">
              <input type="checkbox" v-model="form.private"
                class="h-5 w-5 text-gray-700 rounded focus:ring-gray-600 dark:bg-gray-700 dark:border-gray-500" />
              <span class="text-xs font-medium text-gray-700 dark:text-gray-100">Private</span>
            </label>
          </div>
        </div>


        <div v-if="form.newTicket"
          class="mt-6 p-4 border rounded-md bg-yellow-50 dark:bg-yellow-900 dark:border-yellow-700">
          <h4 class="text-sm font-semibold text-yellow-700 dark:text-yellow-200 mb-2">
            New Ticket Added (Unsaved)
          </h4>
          <div class="text-xs dark:text-gray-100">
            <strong>Description:</strong>
            <div class="ml-3 mb-1" v-html="renderMarkdown(form.newTicket.description, { compact: true, maxLines: 15 })"></div>
            <strong>Tech:</strong> {{ getTechName(form.newTicket.techid) }}<br>
            <strong>Type:</strong> {{ getTicketType(form.newTicket.tickettypeid) }}<br>
            <strong>Est. Time:</strong> {{ form.newTicket.est_time }} hrs<br>
            <strong>Promised:</strong> {{ form.newTicket.promised_date }} @ {{ form.newTicket.promised_time }}<br>
            <strong>Notify Tech:</strong> {{ form.newTicket.ticket_tech_notify ? 'Yes' : 'No' }},
            <strong>Notify Client:</strong> {{ form.newTicket.ticket_client_notify ? 'Yes' : 'No' }}
            <strong>Internal:</strong> {{ form.newTicket.internal ? 'Yes' : 'No' }}
            <strong>Do Not Publish:</strong> {{ form.newTicket.unpublished ? 'Yes' : 'No' }}
          </div>
        </div>

        <div class="md:col-span-2 text-center mt-6 flex space-x-4">
          <button @click="submitForm"
            class="px-6 py-2 border border-transparent text-base font-medium rounded-md text-white bg-green-500 hover:bg-green-700">
            {{ isEdit ? 'Update' : 'Save' }}
          </button>
          <button @click="cancel"
            class="px-6 py-2 border border-transparent text-base font-medium rounded-md text-white bg-red-500 hover:bg-red-700">
            Cancel
          </button>
        </div>
      </div>

      <div class="md:col-span-2">
        <div v-if="form.company_id" class="mt-8 dark:bg-gray-800 dark:border-2 dark:border-gray-800">
          <h3 class="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-2">Select a Ticket</h3>

          <div class="flex mb-4">
            <input v-model="filters.keywords" type="text" placeholder="Search tickets (closed tickets included)..."
              class="text-xs px-3 py-2 rounded-md border w-60 dark:bg-gray-600 dark:border-gray-400 dark:text-white"
              @input="debouncedFetchTickets" />
            <button @click="clearTicketSelection"
              class="ml-3 px-3 py-2 text-xs rounded-md bg-gray-300 hover:bg-gray-400 dark:bg-gray-700 dark:text-white">
              Do not link to a ticket
            </button>
            <button @click="loadModalData"
              class="ml-2 px-3 py-1 rounded bg-yellow-500 hover:bg-yellow-600 text-white text-sm">
              {{ form.newTicket ? 'Edit New Ticket' : 'Create New Ticket' }}
            </button>
          </div>

          <div v-if="selectedTicketId"
            class="mb-3 px-3 py-2 rounded-md bg-blue-50 border border-blue-200 text-xs dark:bg-gray-700 dark:border-gray-500 dark:text-gray-100">
            <div class="flex items-start justify-between gap-3">
              <div class="min-w-0">
                <div class="font-semibold">
                  Linked to ticket #{{ selectedTicketId }}
                  <span v-if="selectedTicket && selectedTicket.closed"
                    class="ml-1 inline-block rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide bg-gray-200 text-gray-700 dark:bg-gray-600 dark:text-gray-100">Closed</span>
                  <span v-if="!selectedTicket" class="font-normal italic text-gray-500 dark:text-gray-300">{{
                    selectedTicketLoading ? '— loading details…' : '— details not available' }}</span>
                </div>
                <div v-if="selectedTicket" class="mt-1 flex flex-wrap gap-x-4 gap-y-1">
                  <span><span class="text-gray-500 dark:text-gray-300">Requester:</span>
                    {{ selectedTicket.requesterName || selectedTicket.requester || '—' }}</span>
                  <span><span class="text-gray-500 dark:text-gray-300">Assignee:</span>
                    {{ selectedTicket.assigned_to || '—' }}</span>
                  <span><span class="text-gray-500 dark:text-gray-300">Submitted:</span>
                    {{ selectedTicket.submitted || '—' }}</span>
                </div>
                <div v-if="selectedTicket" class="mt-1 text-gray-700 dark:text-gray-200"
                  v-html="renderMarkdown(selectedTicket.description, { compact: true, maxLines: 15 })"></div>
              </div>
              <button @click="clearTicketSelection"
                class="shrink-0 px-3 py-1 rounded-md bg-red-500 hover:bg-red-600 text-white">Unlink ✖</button>
            </div>
          </div>

          <table class="w-full text-sm text-left text-gray-500 dark:text-gray-400 border-separate border-spacing-y-3">
            <thead class="text-xs text-gray-700 uppercase bg-gray-100 dark:bg-gray-700 dark:text-gray-400">
              <tr>
                <th class="px-4 py-3">Action</th>
                <th class="px-4 py-3 cursor-pointer" @click="sortTickets('id')"> <span
                    class="inline-flex items-center">ID <span :class="getSortClass('id')"></span></span></th>
                <th class="px-4 py-3 cursor-pointer" @click="sortTickets('requester')"><span
                    class="inline-flex items-center">Requester <span :class="getSortClass('requester')"></span></span>
                </th>
                <th class="px-4 py-3 cursor-pointer" @click="sortTickets('assigned_to')"><span
                    class="inline-flex items-center">Assignee <span :class="getSortClass('assigned_to')"></span></span>
                </th>
                <th class="px-4 py-3 cursor-pointer" @click="sortTickets('submitted')"><span
                    class="inline-flex items-center">Submitted <span :class="getSortClass('submitted')"></span></span>
                </th>
                <th class="px-4 py-3">Description</th>
              </tr>
            </thead>
            <tbody class="bg-white dark:bg-gray-600 rounded-lg">
              <tr v-for="ticket in tickets" :key="ticket.id"
                class="bg-white dark:bg-gray-700 shadow-sm rounded-md transition hover:shadow-md dark:hover:bg-gray-600">
                <td class="px-4 py-2">
                  <button :class="[
                    'px-3 py-1 text-xs font-medium rounded-md transition',
                    selectedTicketId === ticket.id
                      ? 'bg-red-500 text-white hover:bg-red-600'
                      : 'bg-green-500 text-white hover:bg-green-600'
                  ]" @click="toggleTicketSelection(ticket.id)">
                    {{ selectedTicketId === ticket.id ? 'Unlink ✖' : 'Select ✔' }}
                  </button>
                </td>
                <td class="px-4 py-2 font-medium text-gray-900 dark:text-white">
                  {{ ticket.id }}
                  <span v-if="ticket.closed"
                    class="ml-1 inline-block rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide bg-gray-200 text-gray-700 dark:bg-gray-600 dark:text-gray-100">Closed</span>
                </td>
                <td class="px-4 py-2">{{ ticket.requesterName || ticket.requester }}</td>
                <td class="px-4 py-2">{{ ticket.assigned_to }}</td>
                <td class="px-4 py-2">{{ ticket.submitted }}</td>
                <td class="px-4 py-2 align-top">
                  <div v-html="renderMarkdown(ticket.description, { compact: true, maxLines: 12 })"></div>
                </td>
              </tr>
              <tr v-if="!tickets.length">
                <td colspan="6" class="px-4 py-3 text-xs italic text-gray-500 dark:text-gray-300">
                  {{ ticketsLoading ? 'Loading tickets…' : 'No tickets match this search.' }}
                </td>
              </tr>
            </tbody>
          </table>

          <div v-if="ticketTotal" class="flex items-center justify-between mt-2 text-xs dark:text-gray-200">
            <span>
              Showing {{ ticketRangeStart }}–{{ ticketRangeEnd }} of {{ ticketTotal }}
            </span>
            <span class="space-x-2">
              <button @click="changeTicketPage(-1)" :disabled="ticketPage <= 1 || ticketsLoading"
                class="px-3 py-1 rounded-md bg-gray-200 hover:bg-gray-300 disabled:opacity-40 disabled:cursor-not-allowed dark:bg-gray-700 dark:hover:bg-gray-600">
                Prev
              </button>
              <span>Page {{ ticketPage }} of {{ ticketLastPage }}</span>
              <button @click="changeTicketPage(1)" :disabled="ticketPage >= ticketLastPage || ticketsLoading"
                class="px-3 py-1 rounded-md bg-gray-200 hover:bg-gray-300 disabled:opacity-40 disabled:cursor-not-allowed dark:bg-gray-700 dark:hover:bg-gray-600">
                Next
              </button>
            </span>
          </div>
        </div>
      </div>
    </div>
    <CreateTicketModal :isVisible="showCreateTicketModal" :techs="techs" :userId="userId" :ticketTypes="ticketTypes"
      @created="ticketCreated" @close="showCreateTicketModal = false" />
  </div>
</template>
<script>
import { requesterCompanyLabel, requesterCompanyTitle } from '@/utils/requesterCompanies';
import { searchRequesters } from '@/utils/requesterSearch';
import axios from 'axios';
import { useRoute, useRouter } from 'vue-router';
import '@toast-ui/editor/dist/toastui-editor.css';
import CreateTicketModal from '@/views/modals/CreateTicketModal.vue';
import '@toast-ui/editor/dist/theme/toastui-editor-dark.css';
import { Editor } from '@toast-ui/editor';
import { renderMarkdown } from '@/utils/markdown';
import moment from 'moment-timezone';
import ThemeService from '@/services/ThemeService';
import { useToast } from "vue-toastification";
let mapsPromise = null;
function loadGoogleMaps(key) {
  if (window.google?.maps) return Promise.resolve(window.google.maps);
  if (mapsPromise) return mapsPromise;

  mapsPromise = new Promise((resolve, reject) => {
    const s = document.createElement('script');
    s.src = `https://maps.googleapis.com/maps/api/js?key=${key}&v=weekly&libraries=places`;
    s.async = true;
    s.defer = true;
    s.onload = () => resolve(window.google.maps);
    s.onerror = (e) => reject(e);
    document.head.appendChild(s);
  });
  return mapsPromise;
}
export default {
  components: {
    CreateTicketModal
  },
  setup() {
    const toast = useToast();
    return { toast };
  },
  data() {
    return {
      form: {
        id: null,
        company_id: '',
        companyname: '',
        contact_id: '',
        requester: '',
        tech_id: '',
        jobtype_id: '',
        date: '',
        total_time: 0,
        IA: 0,
        description: '',
        companyVehicle: '',
        startNow: false,
        publish: false,
        is911: false,
        private: false,
        ticket_id: 0,
        officeLocationInput: '',
        destinationLocationInput: '',
        selectedOffice: null,
        selectedOfficeId: 0,
        selectedDest: null,
        selectedDestId: 0,
        tripType: 'none',
        miles: 0,
        travelTime: 0,
        manualOffice: false,
        manualDest: false,
        newTicket: null,
      },
      clientNotes: {
        notes: '',
        warning: ''
      },
      dropdownState: {
        client: { index: -1, items: [], visible: false },
        requester: { index: -1, items: [], visible: false },
        office: { index: -1, items: [], visible: false },
        dest: { index: -1, items: [], visible: false }
      },
      clients: [],
      requesters: [],
      clientrequesters: [],
      techs: [],
      jobTypes: [],
      showCreateTicketModal: false,
      techs: [],
      ticketTypes: [],
      officeLocations: [],
      googleOfficeResults: [],
      officeSearchDebounce: null,
      googleClientResults: [],
      destSearchDebounce: null,
      clientLocations: [],
      userId: localStorage.getItem('user_id'),
      userroles: [80],
      isEdit: false,
      jobId: null,
      activeClients: 'true',
      editor: null,
      tickets: [],
      ticketSortKey: 'submitted',
      ticketSortOrder: 'DESC',
      selectedTicketId: null,
      // The linked ticket's own row. Held separately from `tickets` because with the
      // picker paginated it is usually not on the page being shown — in edit mode it
      // may be hundreds of pages back.
      selectedTicket: null,
      selectedTicketLoading: false,
      // Bumped per picker request so a slow earlier response can't overwrite a newer
      // one when sorting and paging are clicked in quick succession.
      ticketRequestSeq: 0,
      // Picker pagination. The list used to be fetched with itemsPerPage:'all';
      // it is now a page at a time off ticket/picker.
      ticketPage: 1,
      // Echoed back by the server, which clamps the page size — don't assume the
      // value we asked for was the one applied.
      ticketPerPage: 25,
      ticketLastPage: 1,
      ticketTotal: 0,
      ticketsLoading: false,
      filters: {
        status: 'Open',
        keywords: '',
        tab: 'all',
        itemsPerPage: 25,
      },
      userlocation_id: 0,
      validationErrors: {}
    };
  },
  async created() {
    this.$updateLoading(1);
    const route = useRoute();
    this.jobId = route.params.jobId;
    const ticketIdFromQuery = route.query.ticketId;

    this.isEdit = !!this.jobId;
    if (this.isEdit) {
      await this.fetchJobDetails();
      await this.fetchInitialData(this.form.company_id);
    } else {
      if (ticketIdFromQuery) {
        await this.loadFromTicket(ticketIdFromQuery);
        await this.fetchInitialData(this.form.company_id);
      } else {
        await this.fetchInitialData();
      }
      const phoenixNow = moment.tz("America/Phoenix");
      this.form.date = phoenixNow.format('YYYY-MM-DD');
    }
    this.$nextTick(() => {
      // $refs.toastEditor is null if the component unmounted before nextTick
      // fired (route change mid-load). Toast UI dereferences `el.innerHTML`
      // immediately and crashes — bail out instead.
      if (!this.$refs.toastEditor) return;
      this.editor = new Editor({
        el: this.$refs.toastEditor,
        height: '500px',
        initialEditType: 'markdown',
        previewStyle: 'vertical',
        usageStatistics: false,
        initialValue: this.fixMarkdownIndentation(this.form.description || ''),
        theme: this.isdark ? 'dark' : 'default',
        toolbarItems: [
          ['heading', 'bold', 'italic', 'strike'],
          ['hr', 'quote'],
          ['ul', 'ol', 'task', 'indent', 'outdent'],
          ['table', 'link'],
          ['code', 'codeblock'],
          ['scrollSync'],
        ],
        hooks: {
          addImageBlobHook: () => false
        }
      });
      this.focusClientInput();
    });
    this.$updateLoading(-1);
  },
  async mounted() {
    this.debouncedFetchTickets = this.debounce(() => this.filterTickets({ spinner: true }), 1000);
  },
  beforeUnmount() {
    if (this.editor) this.editor.destroy();
  },
  methods: {
    renderMarkdown,
    requesterCompanyLabel,
    requesterCompanyTitle,
    async fetchJobDetails() {
      try {
        const { data } = await axios.get(`job/${this.jobId}`);
        if (this.isPrivateJobForOtherUser(data)) {
          this.toast.error('You do not have permission to view this private job');
          this.isEdit = false;
          this.jobId = null;
          const phoenixNow = moment.tz("America/Phoenix");
          this.form = {
            ...this.form,
            id: null,
            total_time: 0,
            IA: 0,
            description: '',
            startNow: false,
            publish: false,
            is911: false,
            private: false,
            ticket_id: 0,
            newTicket: null,
            date: phoenixNow.format('YYYY-MM-DD'),
          };

          this.selectedTicketId = null;

          if (this.editor) this.editor.setMarkdown('');
          this.$router.replace('/job');

          return;
        }
        this.form.id = data.id;
        this.form.company_id = data.company_id;
        this.form.companyname = data.client?.companyname || '';
        this.form.contact_id = data.contact_id;
        this.form.requester = data.ticket?.requester || data.ticket?.contact?.name || data.contact?.name || '';
        if (this.form.requester != '') {
          this.form.requester = this.form.requester + ' (' + data?.contact?.user_type + ')';
        }
        this.form.tech_id = data.ticket?.tech_id || '';
        this.form.jobtype_id = data.jobtype_id;
        this.form.date = data.jdate;
        this.form.total_time = parseFloat(data.totaltime || 0).toFixed(2);
        this.form.IA = parseFloat(data.ignoranceadjustment || 0).toFixed(2);
        this.form.description = data.description;
        this.form.startNow = data.starttime && data.starttime !== '0000-00-00 00:00:00';
        this.form.publish = !!data.publish_to_portal;
        this.form.is911 = data.is911 == 'true' ? true : false;
        this.form.private = data.private === 1;
        this.form.ticket_id = data.ticket_id ?? 0;
        this.form.companyVehicle = data.companyvehicle ?? 'no';
        this.form.selectedDestId = data.location_id || 0;
        this.form.selectedOfficeId = data.mileagesourcelocationid || 0;
        this.selectedTicketId = data.ticket_id ?? null;


        this.form.tripType = data.triptype_id === 1 ? 'oneWay' : data.triptype_id === 2 ? 'round' : 'none';
        this.form.miles = parseFloat(data.miles || 0).toFixed(2);
        this.form.travelTime = parseFloat(data.traveltime || 0).toFixed(2);
        if (this.editor) this.editor.setHTML(this.fixMarkdownIndentation(data.description || ''));
      } catch (err) {
        console.error('Failed to fetch job details:', err);
      }
    },
    async fetchInitialData(companyId = null) {
      try {
        // The system-wide requesters list is large (thousands of rows) and only powers
        // typeahead search, so we fire it independently rather than awaiting it below —
        // it must not hold up the page's loading spinner ($updateLoading). It populates
        // this.requesters when it lands, which is typically well before the user has
        // selected a client and reached the Requester field.
        axios.get(`requesters/`)
          .then(res => { this.requesters = res.data; })
          .catch(err => console.error("There was an error fetching requesters:", err));

        const baseCalls = [
          axios.get(`job/types`),
          axios.get(`office-locations`),
          axios.get(`user/profile/${this.userId}`),
          axios.get(`user/role/${this.userId}`)
        ];

        const [, [jobTypesRes, officeLocsRes, profileRes, roleRes]] = await Promise.all([
          this.fetchClients(),
          Promise.all(baseCalls),
        ]);
        this.userroles = roleRes.data.roles;
        this.jobTypes = jobTypesRes.data;
        this.officeLocations = officeLocsRes.data;
        this.officeLocations.push({ id: 'other', location_name: 'Other', address: '' });

        if (!this.isEdit) {
          this.form.jobtype_id = profileRes.data.jobtype_id;
          this.userlocation_id = profileRes.data.officelocation;
        }
        if (!this.isEdit && !companyId && !this.form.company_id
          && this.clients.length === 1
          && this.userroles.some(r => [60, 70, 80].includes(r))) {
          this.setClient(this.clients[0]);
          companyId = this.clients[0].id;
        }
        if (!companyId) {
          // No client yet: the job-locations list only feeds the destination dropdown,
          // so let it land in the background rather than blocking the spinner.
          //
          // The client-scoped counterpart of this branch used to live here too, and
          // ran in addition to the `form.company_id` watcher — every caller that
          // passes a companyId (fetchJobDetails, loadFromTicket, the single-client
          // auto-select) sets form.company_id first, so the watcher had already
          // issued the same requests. loadClientScopedData() is now the only place
          // that fetches them.
          axios.get(`clients/job-locations`)
            .then(res => { this.clientLocations = res.data; })
            .catch(err => console.error("There was an error fetching job locations:", err));

          const defaultLoc = this.officeLocations.find(
            loc => loc.id == (this.userlocation_id || 6971)
          );
          if (defaultLoc) {
            this.setOfficeLocation(defaultLoc);
          }
        }
      } catch (error) {
        console.error('Error fetching initial data:', error);
      }
    },
    /**
     * Everything that depends on which client is selected: the requester dropdown,
     * the destination-location dropdown, the ticket picker and the client notices.
     *
     * Deliberately fires each request independently and does NOT touch
     * $updateLoading. Selecting a client is the first thing a user does on this page
     * (the client input is auto-focused on load), and the loading overlay used to be
     * raised for the duration of all four calls — which meant the rest of the form
     * was unreachable exactly when someone was trying to fill it in. Each response
     * populates its own target reactively as it lands; nothing here gates the form.
     */
    loadClientScopedData(companyId) {
      if (!companyId) return;

      // Drop the previous client's people now, so the requester typeahead doesn't
      // rank them first for the new client while this list is still loading.
      this.clientrequesters = [];
      axios.get(`requesters/${companyId}`)
        .then(res => { this.clientrequesters = res.data; })
        .catch(err => console.error("There was an error fetching client requesters:", err));

      axios.get(`clients/job-locations/${companyId}`)
        .then(res => {
          this.clientLocations = res.data;
          // In edit mode the saved destination is resolved from clientLocations,
          // so re-apply the saved selections once the list has arrived.
          if (this.isEdit) this.applySavedLocationSelections();
        })
        .catch(err => console.error("There was an error fetching client locations:", err));

      // No spinner here — see filterTickets(). This is the automatic refresh that
      // follows picking a client, not something the user is waiting on.
      this.filterTickets();

      axios.get('clients/notes', { params: { id: companyId } })
        .then(res => {
          const data = res.data || {};
          this.clientNotes = {
            notes: (data.notes || '').trim(),
            warning: (data.warning || '').trim()
          };
        })
        .catch(err => {
          console.error('Error fetching client notes:', err);
          this.clientNotes = { notes: '', warning: '' };
        });
    },
    async loadFromTicket(ticketId) {
      try {
        const { data } = await axios.get(`ticket/${ticketId}`);

        // Fill in form fields
        this.form.ticket_id = data.id;
        this.selectedTicketId = data.id;
        this.form.company_id = data.company_id;
        this.form.private = data.unpublished == 1;
        this.form.companyname = data.client?.companyname || '';
        this.form.contact_id = data.contact_id;
        this.form.requester = data.requester || '';
        this.form.startNow = true;

      } catch (err) {
        console.error("Failed to load ticket data:", err);
        alert("Invalid ticket ID. Please try again.");
      }
    },
    async loadModalData() {
      if (!this.form.company_id) {
        alert("Please select a client first.");
        return;
      }

      this.$updateLoading(1);
      try {
        const [techRes, typeRes] = await Promise.all([
          axios.get(`user/active/${this.form.company_id}`),
          axios.get(`ticket/get-types/${this.form.company_id}`)
        ]);

        this.techs = techRes.data;
        this.ticketTypes = typeRes.data;
        this.showCreateTicketModal = true;
      } catch (err) {
        console.error("Failed to load modal data:", err);
      } finally {
        this.$updateLoading(-1);
      }
    },
    async fetchClients() {
      try {
        const clientsResponse = await axios.get('clients/', { params: { include_inactive: 1 } });
        this.clients = clientsResponse.data;
      } catch (error) {
        console.error("There was an error fetching clients:", error);
      }
    },
    async submitForm() {
      this.form.description = this.editor.getMarkdown();
      if (!this.validateForm()) {
        return;
      }
      this.$updateLoading(1);
      try {
        if (this.isEdit) {
          await axios.post(`job/save/${this.jobId}`, this.form);
        } else {
          await axios.post('job/save', this.form);
        }
        this.$router.push('/jobs');
      } catch (error) {
        console.error('Error saving job:', error);
        alert('Failed to save job. Please check your input and try again.');
      } finally {
        this.$updateLoading(-1);
      }
    },
    isPrivateJobForOtherUser(job) {
      const isPrivate = job?.private == 1;
      const isUnpublishedTicket = job?.ticket?.unpublished == 1;

      return (isPrivate || isUnpublishedTicket) && (parseInt(job?.user_id) !== parseInt(this.userId));
    },
    focusClientInput() {
      if (!this.isEdit) {
        this.$nextTick(() => {
          setTimeout(() => {
            const el = this.$refs.clientInput;
            (Array.isArray(el) ? el[0] : el)?.focus?.();
          }, 0);
        });
      }
    },
    applySavedLocationSelections() {
      const officeId = Number(this.form.selectedOfficeId || 0);
      const destId = Number(this.form.selectedDestId || 0);

      if (officeId > 0 && Array.isArray(this.officeLocations) && !this.form.selectedOffice) {
        const office = this.officeLocations.find(l => Number(l.id) === officeId);
        if (office) this.setOfficeLocation(office, true);
      }

      if (destId > 0 && Array.isArray(this.clientLocations) && !this.form.selectedDest) {
        const dest = this.clientLocations.find(l => Number(l.id) === destId);
        if (dest) this.setDestinationLocation(dest, true);
      }
    },
    getTechName(techId) {
      const tech = this.techs.find(t => t.id == techId);
      return tech ? tech.name : 'Unknown';
    },
    getTicketType(typeId) {
      const type = this.ticketTypes.find(t => t.id == typeId);
      return type ? type.description : 'Unknown';
    },
    setOfficeLocation(loc, from = false) {
      this.form.selectedOffice = loc;
      this.form.selectedOfficeId = ((loc.source === 'aim' && loc.id !== 'other') ? loc.id : 0);
      this.form.officeLocationInput = `${loc.location_name} - ${loc.full_address || loc.address || ''}`;
      this.dropdownState.office.visible = false;
      if (loc.source === 'google') {
        this.form.manualOffice = true;
        this.form.miles = 0;
        this.form.travelTime = 0;
      } else if (loc.id === 'other') {
        this.form.manualOffice = true;
        this.form.miles = 0;
        this.form.travelTime = 0;
      } else {
        this.form.manualOffice = false;
        if (!from) {
          this.calculateMileageViaMapsJS();
        }
      }
    },
    setDestinationLocation(loc, from = false) {
      this.form.selectedDest = loc;
      this.form.selectedDestId = (loc.source === 'google' ? 0 : loc.id);
      this.form.destinationLocationInput = loc.location_name + ' - ' + loc.address;
      this.dropdownState.dest.visible = false;
      if (loc.id === 'other') {
        this.form.manualDest = true;
        this.form.miles = 0;
        this.form.travelTime = 0;
      } else {
        this.form.manualDest = false;
        if (!from) {
          this.calculateMileageViaMapsJS();
        }
      }
    },
    validateForm() {
      const errors = {};

      if (!this.form.company_id) errors.company_id = true;
      if (!this.form.description || this.form.description.trim() === '') errors.description = true;
      if (!this.form.jobtype_id) errors.jobtype_id = true;
      // A quick-created ticket takes its requester from this job, and a ticket
      // with no requester breaks Edit Ticket (same rule as New Ticket).
      if (this.form.ticket_id === 'new' && !(Number(this.form.contact_id) > 0)) {
        errors.contact_id = true;
        this.toast.error('Select a requester before saving a job with a new ticket.');
      }

      this.validationErrors = errors;
      return Object.keys(errors).length === 0;
    },
    ticketCreated(ticketData) {
      this.form.ticket_id = 'new';
      this.form.newTicket = ticketData;
    },
    closeOfficeDropdown() {
      this.dropdownState.office.visible = false;
    },
    closeDestDropdown() {
      this.dropdownState.dest.visible = false;
    },
    metersToMiles(m) {
      return +(m / 1609.344).toFixed(1);
    },
    secondsToHours(s) {
      return +(s / 3600).toFixed(2);
    },
    pickShortestRoute(routes) {
      let best = null;
      for (const r of routes) {
        const meters = r.legs.reduce((sum, leg) => sum + (leg.distance?.value || 0), 0);
        if (!best || meters < best.meters) best = { route: r, meters };
      }
      return best?.route || null;
    },

    async calculateMileageViaMapsJS() {
      const src = this.form.selectedOffice;
      const dest = this.form.selectedDest;
      if (!src || !dest || src.id === 'other' || dest.id === 'other') {
        this.form.miles = 0;
        this.form.travelTime = 0;
        return;
      }
      if (this.form.tripType === 'none') {
        this.form.miles = 0;
        this.form.travelTime = 0;
        return;
      }

      try {
        this.$updateLoading?.(1);

        const maps = await loadGoogleMaps(process.env.VUE_APP_GOOGLE_MAPS_KEY);

        const origin = src.full_address || src.address || src.location_name;
        const destination = dest.address || dest.full_address || dest.location_name;

        const svc = new maps.DirectionsService();
        const req = {
          origin,
          destination,
          travelMode: maps.TravelMode.DRIVING,
          provideRouteAlternatives: true
        };

        const resp = await new Promise((resolve, reject) => {
          svc.route(req, (res, status) => {
            if (status === maps.DirectionsStatus.OK) resolve(res);
            else reject(new Error(status));
          });
        });

        if (!resp?.routes?.length) {
          const base = parseFloat(dest.total_mileage) || 0;
          this.form.miles = this.form.tripType === 'round' ? base * 2 : base;
          this.form.travelTime = +(this.form.miles / 30).toFixed(2);
          return;
        }

        const best = this.pickShortestRoute(resp.routes);
        const meters = best.legs.reduce((sum, leg) => sum + (leg.distance?.value || 0), 0);
        const seconds = best.legs.reduce((sum, leg) => sum + (leg.duration?.value || 0), 0);

        let miles = this.metersToMiles(meters);
        let hours = this.secondsToHours(seconds);

        if (this.form.tripType === 'round') {
          miles = +(miles * 2).toFixed(1);
          hours = +(hours * 2).toFixed(2);
        }

        this.form.miles = miles;
        this.form.travelTime = hours;

      } catch (err) {
        console.error('Google Maps JS Directions error:', err);
        // fallback mirrors legacy behavior
        const base = parseFloat(this.form.selectedDest?.total_mileage) || 0;
        this.form.miles = this.form.tripType === 'round' ? base * 2 : base;
        this.form.travelTime = +(this.form.miles / 30).toFixed(2);
      } finally {
        this.$updateLoading?.(-1);
      }
    },
    viewGoogleMap() {
      if (!this.form.selectedOffice || !this.form.selectedDest) return;
      const origin = encodeURIComponent(this.form.selectedOffice.full_address);
      const dest = encodeURIComponent(this.form.selectedDest.address);
      window.open(`https://www.google.com/maps/dir/?api=1&origin=${origin}&destination=${dest}&travelmode=driving`, '_blank');
    },
    toggleTicketSelection(ticketId) {
      const selected = this.tickets.find(t => t.id === ticketId);

      if (this.selectedTicketId === ticketId) {
        this.selectedTicketId = null;
        this.selectedTicket = null;
        this.form.ticket_id = 0;
        this.form.contact_id = '';
        this.form.requester = '';
      } else {
        // Row already in hand, so the watcher below has nothing to fetch.
        this.selectedTicket = selected || null;
        this.selectedTicketId = ticketId;
        this.form.ticket_id = ticketId;
        this.form.private = selected.unpublished == 1;
        if (selected?.contact_id) {
          this.form.contact_id = selected.contact_id;
          this.form.requester = selected.requester || '';
        }
      }
    },
    clearTicketSelection() {
      this.selectedTicketId = null;
      this.selectedTicket = null;
      this.form.ticket_id = 0;
      this.form.contact_id = '';
      this.form.requester = '';
    },
    clearOfficeLocation() {
      this.form.officeLocationInput = '';
      this.form.selectedOffice = null;
      this.form.selectedOfficeId = 0;
      this.form.miles = 0;
      this.form.travelTime = 0;
      this.googleOfficeResults = [];
      this.dropdownState.office.items = [];
      this.dropdownState.office.index = -1;
    },

    clearDestinationLocation() {
      this.form.destinationLocationInput = '';
      this.form.selectedDest = null;
      this.form.selectedDestId = 0;
      this.form.miles = 0;
      this.form.travelTime = 0;

      this.googleClientResults = [];
      this.dropdownState.dest.items = [];
      this.dropdownState.dest.index = -1;
    },
    sortTickets(column) {
      if (this.ticketSortKey === column) {
        this.ticketSortOrder = this.ticketSortOrder === 'asc' ? 'desc' : 'asc';
      } else {
        this.ticketSortKey = column;
        this.ticketSortOrder = 'asc';
      }
      this.filterTickets({ spinner: true });
    },
    getSortClass(column) {
      if (this.ticketSortKey === column) {
        return this.ticketSortOrder.toLowerCase() === 'asc' ? 'sort-asc' : 'sort-desc';
      }
      return '';
    },
    // Re-run the picker query from the first page.
    //
    // `spinner` separates the two kinds of caller. A search, a sort or a page
    // change is something the user just asked for and is waiting on, so those
    // raise the loading overlay. The refresh that follows picking a client is
    // not — the user is heading for the form fields next, and raising the
    // overlay there is what made this page feel unusable.
    filterTickets({ spinner = false } = {}) {
      this.ticketPage = 1;
      this.fetchTicketPage({ spinner });
    },
    // ticket/picker returns only the eight fields this table reads, paginated.
    // The page previously used ticket/getAll with itemsPerPage:'all', which also
    // ran two tab-count queries, eager-loaded eleven relations, post-loaded every
    // job and document, and signed two presigned URLs per attachment — none of
    // which is rendered here.
    fetchTicketPage({ spinner = false } = {}) {
      if (!this.form.company_id) {
        this.tickets = [];
        this.ticketTotal = 0;
        this.ticketLastPage = 1;
        return;
      }

      // The list shows open tickets until the user starts typing. A search widens
      // to closed tickets too, so a job can be logged against a ticket that was
      // already completed. The query is always scoped to the selected client
      // (client_id), which keeps the wider search cheap.
      const searching = this.filters.keywords.trim() !== '';
      const params = {
        client_id: this.form.company_id,
        sort_column: this.ticketSortKey,
        sort_order: this.ticketSortOrder.toUpperCase(),
        page: this.ticketPage,
        ...this.filters,
        status: searching ? 'All' : this.filters.status,
      };

      // Sorting and paging can be clicked faster than the server answers, and
      // responses are not guaranteed to come back in order. Only the newest request
      // is allowed to write to the table.
      const seq = ++this.ticketRequestSeq;
      const isCurrent = () => seq === this.ticketRequestSeq;

      this.ticketsLoading = true;
      if (spinner) this.$updateLoading(1);
      axios.post('ticket/picker', params)
        .then(response => {
          if (!isCurrent()) return;
          const body = response.data || {};
          this.tickets = body.data || [];
          this.ticketPage = body.current_page || 1;
          this.ticketLastPage = body.last_page || 1;
          this.ticketTotal = body.total || 0;
          this.ticketPerPage = body.per_page || this.filters.itemsPerPage;
        })
        .catch(error => {
          console.error('Error fetching tickets:', error);
        })
        .finally(() => {
          // The overlay counter is always balanced, even for a superseded request —
          // skipping it would leave the overlay up forever.
          if (spinner) this.$updateLoading(-1);
          if (isCurrent()) this.ticketsLoading = false;
        });
    },
    // Fetch just the linked ticket, for the banner above the table. Reuses the picker
    // endpoint's `ticket_id` filter so it returns the same shape and obeys the same
    // visibility rules as the list itself.
    fetchSelectedTicket(ticketId) {
      this.selectedTicketLoading = true;
      axios.post('ticket/picker', { ticket_id: ticketId, itemsPerPage: 1 })
        .then(response => {
          // Ignore a late answer for a ticket that is no longer the selected one.
          if (this.selectedTicketId !== ticketId) return;
          this.selectedTicket = ((response.data || {}).data || [])[0] || null;
        })
        .catch(error => {
          console.error('Error fetching the linked ticket:', error);
        })
        .finally(() => {
          if (this.selectedTicketId === ticketId) this.selectedTicketLoading = false;
        });
    },
    changeTicketPage(delta) {
      const next = this.ticketPage + delta;
      if (next < 1 || next > this.ticketLastPage) return;
      this.ticketPage = next;
      this.fetchTicketPage({ spinner: true });
    },
    debounce(func, wait, immediate) {
      let timeout;
      return function (...args) {
        const context = this;
        const later = function () {
          timeout = null;
          if (!immediate) func.apply(context, args);
        };
        const callNow = immediate && !timeout;
        clearTimeout(timeout);
        timeout = setTimeout(later, wait);
        if (callNow) func.apply(context, args);
      };
    },
    fixMarkdownIndentation(text) {
      return text
        .split('\n')
        .map(line => {
          // Fix indentation for list items that start with ' - '
          if (line.startsWith(' - ')) {
            line = '    - ' + line.slice(3);
          }

          // Fix markdown headings without space, e.g., '###Heading' → '### Heading'
          line = line.replace(/^(#{1,6})([^\s#])/g, '$1 $2');

          return line;
        })
        .join('\n');
    },
    cancel() {
      this.$router.push('/jobs');
    },
    onKeyDown(field, direction) {
      const state = this.dropdownState[field];
      if (!state.visible || state.items.length === 0) return;

      if (direction === 'down') {
        state.index = (state.index + 1) % state.items.length;
      } else if (direction === 'up') {
        state.index = (state.index - 1 + state.items.length) % state.items.length;
      }

      this.$nextTick(() => {
        const container = this.$el.querySelector(`.scrollable-requester-list[data-field="${field}"]`);
        const activeItem = container?.querySelector('.active-item');
        activeItem?.scrollIntoView({ block: 'nearest' });
      });
    },
    onEnter(field, selectFn, nextRef = null, ...args) {
      const state = this.dropdownState[field];
      if (state.index >= 0 && state.index < state.items.length) {
        selectFn(state.items[state.index], ...args);
        state.index = -1;
        if (nextRef) {
          this.$nextTick(() => this.$refs[nextRef]?.focus());
        }
      }
    },
    closeClientDropdown() {
      this.dropdownState.client.visible = false;
    },
    setClient(client) {
      this.form.company_id = client.id;
      this.form.companyname = client.companyname;
      this.dropdownState.client.visible = false;
    },
    clearClient() {
      this.form.company_id = '';
      this.form.companyname = '';
    },
    closeReqDropdown() {
      this.dropdownState.requester.visible = false;
    },
    setReq(req) {
      this.form.requester = req.name + ' (' + req.user_type + ')';
      this.form.contact_id = req.id;
      this.dropdownState.requester.visible = false;
    },
    clearReq() {
      this.form.requester = '';
      this.form.contact_id = '';
    },
    onDestinationInputChange() {
      const term = this.form.destinationLocationInput || '';

      if (this.destSearchDebounce) {
        clearTimeout(this.destSearchDebounce);
      }

      if (term.length < 3) {
        this.googleClientResults = [];
        return;
      }

      this.destSearchDebounce = setTimeout(() => {
        this.searchClientInGoogle(term);
      }, 400);
    },
    async searchClientInGoogle(input) {
      try {
        await loadGoogleMaps(process.env.VUE_APP_GOOGLE_MAPS_KEY);
        const { AutocompleteSuggestion } = await google.maps.importLibrary('places');

        const { suggestions } =
          await AutocompleteSuggestion.fetchAutocompleteSuggestions({
            input,
            includedRegionCodes: ['us']
          });

        this.googleClientResults = (suggestions || []).slice(0, 5).map((s, idx) => {
          const place = s.placePrediction;
          const main = place.mainText?.text || '';
          const secondary = place.secondaryText?.text || '';
          const label = secondary ? `${main}, ${secondary}` : main;

          return {
            id: place.placeId,
            _key: `google-client-${place.placeId}-${idx}`,
            source: 'google',
            location_name: main,
            full_address: label,
            address: label,
            place_id: place.placeId,
          };
        });
      } catch (err) {
        console.error('Google Places search error (client):', err);
        this.googleClientResults = [];
      }
    },
    onOfficeInputChange() {
      const term = this.form.officeLocationInput || '';

      if (this.officeSearchDebounce) {
        clearTimeout(this.officeSearchDebounce);
      }

      if (term.length < 3) {
        this.googleOfficeResults = [];
        return;
      }

      this.officeSearchDebounce = setTimeout(() => {
        this.searchOfficeInGoogle(term);
      }, 400);
    },
    async searchOfficeInGoogle(input) {
      try {
        await loadGoogleMaps(process.env.VUE_APP_GOOGLE_MAPS_KEY);
        const { AutocompleteSuggestion } =
          await google.maps.importLibrary('places');
        const { suggestions } =
          await AutocompleteSuggestion.fetchAutocompleteSuggestions({
            input,
            includedRegionCodes: ['us']
          });

        this.googleOfficeResults = (suggestions || []).slice(0, 5).map((s, idx) => {
          const place = s.placePrediction;
          const main = place.mainText?.text || '';
          const secondary = place.secondaryText?.text || '';
          const label = secondary ? `${main}, ${secondary}` : main;

          return {
            id: place.placeId,
            _key: `google-${place.placeId}-${idx}`,
            source: 'google',
            location_name: main,
            full_address: label,
            address: label,
            place_id: place.placeId,
          };
        });
      } catch (err) {
        console.error('Google Places search error (new API):', err);
        this.googleOfficeResults = [];
      }
    },
  },
  watch: {
    // Covers the cases where the link is not the result of clicking a row: editing a
    // job that already has one, or arriving from a ticket.
    selectedTicketId(newVal) {
      if (!newVal) {
        this.selectedTicket = null;
        return;
      }
      if (this.selectedTicket && this.selectedTicket.id === newVal) return;
      this.selectedTicket = null;
      this.selectedTicketLoading = true;
      this.fetchSelectedTicket(newVal);
    },

    'form.company_id': function (newVal, oldVal) {
      if (!newVal) return;
      this.loadClientScopedData(newVal);
    },

    isdark(newVal) {
      console.log('heyyyyyy (dark mode change detected): ', this.isdark);
      if (this.editor) {
        this.$nextTick(() => {
          this.editor = new Editor({
            el: this.$refs.toastEditor,
            height: '500px',
            initialEditType: 'markdown',
            previewStyle: 'vertical',
            usageStatistics: false,
            initialValue: this.fixMarkdownIndentation(this.editor.getMarkdown() || this.form.description || ''),
            theme: this.isdark ? 'dark' : 'default',
            toolbarItems: [
              ['heading', 'bold', 'italic', 'strike'],
              ['hr', 'quote'],
              ['ul', 'ol', 'task', 'indent', 'outdent'],
              ['table', 'link'],
              ['code', 'codeblock'],
              ['scrollSync'],
            ],
            hooks: {
              addImageBlobHook: () => false
            }
          });
          this.focusClientInput();
        });
      }
    }
  },
  computed: {
    ticketRangeStart() {
      return this.ticketTotal ? (this.ticketPage - 1) * this.ticketPerPage + 1 : 0;
    },
    ticketRangeEnd() {
      return Math.min(this.ticketPage * this.ticketPerPage, this.ticketTotal);
    },
    filteredClients() {
      const filtered = this.form.companyname
        ? this.clients.filter((e) =>
          e.companyname
            .toLowerCase()
            .includes(this.form.companyname.toLowerCase())
        )
        : this.clients;
      this.dropdownState.client.items = filtered;
      return filtered;
    },
    filteredRequesters() {
      const allNamed = this.requesters.filter(req => req.name && req.name.trim() !== '');
      const filtered = this.form.requester
        // Typing: search across ALL requesters system-wide, the selected client's
        // own people listed first.
        ? searchRequesters(allNamed, this.form.requester, this.form.company_id, { clientList: this.clientrequesters })
        // Empty: this client's requesters when a client is selected, else all.
        : (this.form.company_id ? this.clientrequesters : allNamed);
      this.dropdownState.requester.items = filtered;
      return filtered;
    },
    filteredOfficeLocations() {
      const keyword = this.form.officeLocationInput?.toLowerCase() || '';
      const aim = keyword
        ? this.officeLocations.filter(loc => {
          const haystack = `${loc.location_name} - ${loc.full_address || ''}`.toLowerCase();
          return haystack.includes(keyword);
        })
        : this.officeLocations;

      const aimTagged = aim.map(loc => ({
        ...loc,
        source: loc.source || 'aim',
        full_address: loc.full_address || loc.address || ''
      }));

      const googleTagged = this.googleOfficeResults || [];

      const combined = [...aimTagged, ...googleTagged];

      this.dropdownState.office.items = combined;
      return combined;
    },

    filteredClientLocations() {
      const keyword = this.form.destinationLocationInput?.toLowerCase() || '';

      const aim = keyword
        ? this.clientLocations.filter((loc) => {
          const haystack = `${loc.location_name} - ${loc.address}`.toLowerCase();
          return haystack.includes(keyword);
        })
        : this.clientLocations;

      const aimTagged = aim.map((loc) => ({
        ...loc,
        source: loc.source || 'aim',
      }));

      const googleTagged = this.googleClientResults || [];

      const combined = [...aimTagged, ...googleTagged];

      this.dropdownState.dest.items = combined;
      return combined;
    },

    isdark() {
      return ThemeService.isDark.value;
    }
  }
};
</script>

<style>
.sort-asc::after {
  content: '▲';
  font-size: 0.75rem;
  margin-left: 0.5rem;
}

.sort-desc::after {
  content: '▼';
  font-size: 0.75rem;
  margin-left: 0.5rem;
}
</style>
